/**
 * RWBR — THE WINTER.
 *
 * The season ended in November. This is what happens before February, and it
 * is the module that turns a game into a career: cards change, drivers leave,
 * rookies arrive, and teams that could not pay their bills stop existing.
 *
 * Sagliano PART ONE §§3.2–3.13 (pp. 5–9), in his order:
 *
 *     1. DEMOTION      anyone who failed to finish 75% of his races is
 *                      unranked and goes first; then the five lowest-placed
 *                      experienced drivers (SAG/5).
 *     2. RE-RATING     for every survivor, an ORDERED chain where each step
 *                      conditions the next — total pedals from final standing,
 *                      gas from standing, brake as the remainder, quality COUNT
 *                      from the pedal total, then which qualities, then stars,
 *                      trophy, fast pit, escape (SAG/6-8).
 *     3. THE GARAGE    teams that ran out of money fold. Their charter is
 *                      bought and re-branded — new name, new colours, new
 *                      owner, clean books.
 *     4. ROOKIES       a new fictional driver for every empty seat, rolled on
 *                      the INAUGURAL tables, because "Rookie drivers use the
 *                      Inaugural Season charts for all ratings. In the second
 *                      season, they are considered regular drivers" (SAG/5).
 *
 * WHY THE ORDER IS LOAD-BEARING. Sagliano's chain is not a set of independent
 * lookups. The pedal total decides which column of the quality table you read;
 * the quality count decides nothing downstream here but everything in the
 * finance layer (sponsorship, salary and automaker support are all keyed to
 * it); average start decides the trophy. Shuffle the steps and you get a
 * different, wrong driver. The same discipline `driverGen.ts` keeps for
 * creation, kept here for re-rating.
 *
 * WHAT IS NOT SAGLIANO'S, AND IS FLAGGED AS SUCH:
 *
 *   • Every percentage-driven step is EVIDENCE-WEIGHTED (D-008). A driver who
 *     faced TROUBLE twice and failed both is not handed ESCAPE 2 and a ruined
 *     career; the observation is blended toward his current rating in
 *     proportion to how much of it there is. Sagliano never hit this because a
 *     human looks at a two-roll sample and uses judgement.
 *   • PIT CREW is a TEAM rating (D-007), read off the shop's aggregate finish
 *     rate rather than one driver's.
 *   • TEAM FAILURE is entirely ours. The guide has no insolvency rule at all —
 *     it has no persistent team balance to be insolvent against. See §3 below.
 *
 * PURITY. `runOffseason` is a pure function of its input. It mutates none of
 * its arguments, builds its own dice from the seed, and returns a complete new
 * roster and garage plus the ledger of everything it did.
 */

import { applyTraits, topUpTraits, TRAITS_BY_CODE } from './traits.ts';
import { Dice } from './dice.ts';
import type { DriverCard, PitCrewGrade, Quality, Trophy } from './types.ts';
import { QUALITIES } from './types.ts';
import type { RuleEvent } from './ruleEvent.ts';
import type { SeasonResult } from './season.ts';
import type { DriverSeasonStats, SeasonStandings } from './standings.ts';
import {
  CAL_BRAKE_PEDALS,
  createDriver,
  lookup,
  rollPercentile,
  QUALITY_BY_DRIVER,
  type GeneratedDriver,
  type RollTable,
} from './driverGen.ts';
import {
  CAR_NUMBERS,
  FIRST_NAMES,
  SURNAMES,
  TEAM_SIZE,
  TRADES,
  type Team,
} from './field.ts';
import { assignInks, liveryFor } from './livery.ts';
import { calibratedNormShift, evidenceWeightedRerate, type BandTable } from './rerate.ts';
import {
  ageOf,
  agingStep,
  CITE_AGING,
  CITE_RETIREMENT,
  licenceLossChance,
  retirementChance,
  rookieAge,
  type AgingStep,
  type CareerStanding,
} from './aging.ts';
import { emptyCareerCarry, type CareerCarry } from './careerCarry.ts';
import type { CareerPrior } from './history/types.ts';
import type { FinanceCarry } from './finance/seasonFinance.ts';
import RERATE_TABLES from '../data/rerate-tables.json';
import { foldMoney, type MoneyEvent } from './finance/money.ts';
import { WEEKLY_RACE_COST_MB, SAGLIANO_BASELINE_RACES } from './finance/tables.ts';

// ---------------------------------------------------------------------------
// Citations
// ---------------------------------------------------------------------------

export const CITE_DEMOTION =
  'SAG/5 — "Demote five experienced drivers that have placed lowest in the standings in addition to the drivers who failed to finish 75% of their races due to DNQ and/or DNF"';
export const CITE_ROOKIE_TABLES =
  'SAG/5 — "Rookie drivers use the Inaugural Season charts for all ratings. In the second season, they are considered regular drivers"';
export const CITE_PEDAL_TOTAL = 'SAG/6 — Re-rating: Total # of Brake and Gas pedals';
export const CITE_GAS = 'SAG/6 — Re-rating: Gas Pedals';
export const CITE_QUALITY_COUNT = 'SAG/7 — Re-rating: Qualities';
export const CITE_QUALITY_ASSIGN = 'SAG/7 — Assigning Qualities (STAR and HEAT are earned, the rest are rolled)';
export const CITE_STARRED = 'SAG/7 — Starred* Qualities on re-rating';
export const CITE_TROPHY = 'SAG/8 — Re-rating: Trophy (or NONE) — by Average Start';
export const CITE_FAST_PIT = 'SAG/8 — Re-rating: Fast Pit';
export const CITE_BRAKE_DRAWN =
  'SAG/6 + D-033 — "After you determine the number of gas pedals, other assignments may be made if desired"; brake drawn from the published card distribution rather than taken as the remainder';
export const CITE_ESCAPE = 'SAG/8 — Re-rating: Escape, evidence-weighted per D-008';
export const CITE_PIT_CREW = 'SAG/8 — Re-rating: Pit Crew (based on RAF%), as a TEAM rating per D-007';
export const CITE_FEUD_WINTER =
  'D-EZ-94 — a feud live at the season\'s end rolls d6 over the winter: 1-3 it cools off, 4-6 it carries into the new season';
export const CITE_TEAM_FAILURE =
  'D-031 — insolvency and charter transfer. Sagliano has no team-failure rule; he has no persistent team balance for one to act on.';

// ---------------------------------------------------------------------------
// SAG/6 — the pedal tables
// ---------------------------------------------------------------------------

/**
 * SAG/6, verbatim. Keyed to FINAL STANDINGS, not to anything the driver's card
 * says — which is the whole point of the system: last season is the only
 * evidence, and a champion is rebuilt as a champion whatever he used to be.
 *
 * The 1st-2nd row is the only one that rolls: "7 (1d6: 1-5)  8 (1d6: 6)".
 */
export function totalPedalsFor(rank: number, d6: number): { total: number; band: string; rolled: boolean } {
  if (rank <= 2) return { total: d6 === 6 ? 8 : 7, band: '1st - 2nd', rolled: true };
  if (rank <= 10) return { total: 6, band: '3rd - 10th', rolled: false };
  if (rank <= 18) return { total: 5, band: '11th - 18th', rolled: false };
  if (rank <= 34) return { total: 4, band: '19th - 34th', rolled: false };
  if (rank <= 42) return { total: 3, band: '35th - 42nd', rolled: false };
  return { total: 2, band: '43rd & below', rolled: false };
}

/** SAG/6, verbatim. */
export function gasPedalsFor(rank: number): { gas: number; band: string } {
  if (rank <= 3) return { gas: 5, band: '1st - 3rd' };
  if (rank <= 18) return { gas: 4, band: '4th - 18th' };
  if (rank <= 34) return { gas: 3, band: '19th - 34th' };
  return { gas: 2, band: '35th & below' };
}

/**
 * SAG/6 — "Then subtract the number of gas pedals from the total number of
 * pedals to get the number of Brake Pedals."
 *
 * Two constraints have to be enforced on top of the subtraction, and neither is
 * stated as a rule — both are inferred from the note beneath the table:
 *
 *   BRAKE CANNOT GO NEGATIVE. A 43rd-place driver gets 2 total pedals and 2 gas
 *   (both from printed rows), leaving 0 brake — fine. But an 11th-place driver
 *   gets 5 total and 4 gas, and a 3rd-place driver gets 6 total and 5 gas. The
 *   arithmetic only breaks at the boundary where a high gas row meets a low
 *   total row, which the standings bands mostly prevent. Clamped anyway, with
 *   the gas pedal giving way, because a car with negative brakes is not a car.
 *
 *   BRAKE CAPS AT 3, AND ONLY ON 8 PEDALS. Verbatim: "You can have 3 brake
 *   pedals only if the driver has a total of 8 gas and brake pedals." Overflow
 *   goes back to the gas pedal.
 */
/**
 * D-033 — CAREERS MOVE A STEP AT A TIME.
 *
 * SAG/6 assigns pedals purely by STANDINGS BAND: everyone from 4th to 18th gets
 * exactly 4 gas, everyone from 19th to 34th gets exactly 3. A driver's existing
 * card is discarded entirely and replaced with his band's number.
 *
 * MEASURED over ten seasons, that flattens the league, and it flattens it after
 * ONE winter:
 *
 *   season          1       2       5      10     target
 *   mean gas     2.45    3.30    3.19    3.17     2.65 (published cards)
 *   mean brake   0.28    0.98    1.02    1.00     0.24
 *   winner's
 *     mean start 10.2    17.2    16.9    15.6     8-12
 *   top-10
 *     starters   56%     44%     36%     36%      45-65%
 *   DNFs/race     8.8     9.3    10.9    10.7
 *
 * Fifteen drivers holding an identical 4-gas 1-brake card is fifteen drivers
 * who cannot be told apart, so qualifying stops predicting anything, winners
 * come from the back, and contact goes up. This is the D-021 calibration fault
 * again, arriving through the re-rate tables instead of the creation tables.
 *
 * RULING: a pedal moves at most ONE step per winter, toward the band SAG/6
 * names. The band is still the target and a driver still gets there — it takes
 * him two or three seasons instead of one afternoon. That is Sagliano's own
 * stated intent, in his words: "over time drivers improve or decline and
 * suddenly (or GRADUALLY) are demoted out of the league."
 *
 * It is also the same principle D-008 already applies to every percentage-driven
 * rating, and for the same reason: a printed table read as an instant snap
 * throws away everything the card already knew.
 *
 * Pass `pedalStepCap: null` to read SAG/6 literally.
 */
function capStep(current: number, target: number, cap: number | null): number {
  if (cap === null) return target;
  if (target > current) return Math.min(target, current + cap);
  if (target < current) return Math.max(target, current - cap);
  return current;
}

export function splitPedals(total: number, gas: number): { gas: number; brake: number; adjusted: string | null } {
  let g = Math.min(gas, total);
  let brake = total - g;
  let adjusted: string | null = null;

  const brakeCap = total >= 8 ? 3 : 2;
  if (brake > brakeCap) {
    const spill = brake - brakeCap;
    brake = brakeCap;
    g += spill;
    adjusted = `brake capped at ${brakeCap} (SAG/6 — "3 brake pedals only if… a total of 8"); ${spill} returned to the gas pedal`;
  }
  if (g !== gas && adjusted === null) adjusted = `gas reduced to ${g}: the standings row asked for more pedals than the total allows`;

  return { gas: g, brake, adjusted };
}

/**
 * SAG/7 — the quality-count table, transposed. Read down the column for the
 * driver's pedal total; the d100 picks the row.
 *
 * Every column is a complete 1-100 partition, audited by test. Columns for 2
 * and 3 pedals cannot produce a 4- or 5-quality driver at all, which is what
 * makes the whole ladder self-consistent: a bad season costs pedals, pedals
 * cost qualities, and qualities cost money.
 */
export const QUALITY_COUNT_BY_PEDALS: Record<number, { min: number; max: number; count: number }[]> = {
  2: [
    { min: 1, max: 10, count: 2 },
    { min: 11, max: 100, count: 1 },
  ],
  3: [
    { min: 1, max: 4, count: 3 },
    { min: 5, max: 52, count: 2 },
    { min: 53, max: 100, count: 1 },
  ],
  4: [
    { min: 1, max: 6, count: 4 },
    { min: 7, max: 44, count: 3 },
    { min: 45, max: 100, count: 2 },
  ],
  5: [
    { min: 1, max: 18, count: 4 },
    { min: 19, max: 100, count: 3 },
  ],
  6: [
    { min: 1, max: 50, count: 4 },
    { min: 51, max: 100, count: 3 },
  ],
  7: [
    { min: 1, max: 50, count: 5 },
    { min: 51, max: 100, count: 4 },
  ],
  8: [
    { min: 1, max: 75, count: 5 },
    { min: 76, max: 100, count: 4 },
  ],
};

export function qualityCountFor(pedalTotal: number, roll: number): { count: number; band: string } {
  const column = QUALITY_COUNT_BY_PEDALS[pedalTotal];
  if (!column) throw new RangeError(`SAG/7 has no column for a ${pedalTotal}-pedal driver`);
  const band = column.find((b) => roll >= b.min && roll <= b.max);
  if (!band) throw new RangeError(`SAG/7 column ${pedalTotal} has a gap at ${roll}`);
  return { count: band.count, band: `${band.min}-${band.max}` };
}

/**
 * SAG/8 — the trophy, by rank on AVERAGE START rather than on points. Ten gold,
 * eleven silver, twenty-two bronze: exactly the 43 cars that make a race.
 *
 * "If one or more DNQ, there is no trophy for the driver." A driver who missed
 * a show is excluded from the ranking before it is drawn, not merely placed
 * low in it — so a fast driver who failed to qualify once loses his trophy
 * outright, which is harsh and is what the page says.
 */
export const TROPHY_RANKS: readonly { upTo: number; trophy: Trophy; label: string }[] = [
  { upTo: 10, trophy: 'gold', label: 'ten drivers with best average start' },
  { upTo: 21, trophy: 'silver', label: 'the next eleven' },
  { upTo: 43, trophy: 'bronze', label: 'the final twenty-two' },
];

export function trophyForStartRank(rank: number): Trophy {
  for (const band of TROPHY_RANKS) if (rank <= band.upTo) return band.trophy;
  return null;
}

/**
 * SAG/8 — Fast Pit, by final standing, on 1d6.
 *
 * Note the shape: every row is a two-way split, and the better half is the
 * larger half in four rows out of six. A driver's crew gets luckier the worse
 * he finished, which reads odd until you notice it is the only thing in the
 * re-rate chain that pulls UP on a bad season. Transcribed as printed.
 */
export function fastPitFor(rank: number, d6: number): { value: number; band: string } {
  if (rank <= 3) return { value: d6 <= 4 ? 9 : 10, band: '1st - 3rd' };
  if (rank <= 10) return { value: d6 <= 2 ? 7 : 8, band: '4th - 10th' };
  if (rank <= 18) return { value: d6 <= 2 ? 5 : 7, band: '11th - 18th' };
  if (rank <= 29) return { value: d6 <= 2 ? 4 : 5, band: '19th - 29th' };
  if (rank <= 43) return { value: d6 <= 2 ? 3 : 4, band: '30th - 43rd' };
  return { value: d6 <= 4 ? 2 : 3, band: 'below 43rd' };
}

const ESCAPE_TABLE = (RERATE_TABLES as Record<string, unknown>).escape as BandTable<number>;
const PIT_CREW_TABLE = (RERATE_TABLES as Record<string, unknown>).pitCrewRaf as BandTable<PitCrewGrade>;

// ---------------------------------------------------------------------------
// Input and output
// ---------------------------------------------------------------------------

export interface OffseasonOptions {
  /**
   * SAG/5 — "Demote five experienced drivers that have placed lowest in the
   * standings". Printed as five; configurable because his league is 43 cars and
   * ours is a 53-driver roster.
   */
  demoteLowest?: number;
  /** SAG/5 — the finishing threshold below which a driver is unranked. */
  finishThreshold?: number;
  /**
   * Minimum races (starts + DNQs) before the finish threshold may end a career.
   * D-008's principle applied to a hard cut: three starts is not evidence.
   */
  minimumJudgedRaces?: number;
  /**
   * D-032 — how many UNRANKED drivers may actually lose their licence in one
   * winter, worst finish rate first. See the note on `demote`.
   */
  maxUnranked?: number;
  /**
   * D-033 — the most a gas or brake pedal may move in one winter. `null` reads
   * SAG/6 literally. See `capStep`.
   */
  pedalStepCap?: number | null;
  /**
   * D-033 — where the BRAKE pedal comes from. `'published'` (default) draws it
   * from the calibrated distribution the printed cards actually show;
   * `'remainder'` reads SAG/6's subtraction literally, which quadruples the
   * league's brakes after one winter and measurably breaks the racing.
   */
  brakeSource?: 'published' | 'remainder';
  /**
   * D-031 — how far under water a team may go before it folds, in Megabucks.
   * Default is one season's fixed cost, so a team dies from sustained failure
   * rather than one bad year.
   */
  insolvencyFloorMb?: number;
  /**
   * D-031 — what happens to the drivers of a failed team. `rebrand` (default)
   * keeps the charter and the seats and changes the name, colours and owner, so
   * the field stays the size it was. `close` retires the charter and the seats
   * with it.
   */
  onFailure?: 'rebrand' | 'close';
  /**
   * D-EZ-3 — the share of the roster carrying an EZRA driver trait. Same default
   * as `buildField`; pass 0 to run a league with no traits at all.
   */
  traitShare?: number;
  /** Season number just completed. Used only for naming and narration. */
  seasonNumber?: number;
  /**
   * D-EZ-93 — how careers end. `'aging'` (default, Howard 4 Oct 2026): drivers
   * age, retire voluntarily, and lose a licence on poor performance by CHANCE —
   * see `aging.ts`. `'fixed'` restores D-032's exactly-thirteen demotions.
   */
  licensing?: 'aging' | 'fixed';
  /**
   * D-EZ-92 — how ESCAPE is judged. `'league'` (default) reads each driver's
   * escape rate against the league's own norm (`calibratedNormShift`), which stops
   * track penalties dragging the whole field down every winter. `'absolute'`
   * reads SAG/8 in a vacuum, as it was before.
   */
  escapeNorm?: 'league' | 'absolute';
  /**
   * D-EZ-92 — how a crew is graded. `'percentile'` (default) ranks every crew
   * by finish rate and hands out grades in the shares the inaugural tables
   * produce, a grade at a time. `'absolute'` reads SAG/8's RAF% bands as before,
   * which slides as DNFs rise.
   */
  crewGrading?: 'percentile' | 'absolute';
}

export interface OffseasonInput {
  seed: number;
  season: SeasonResult;
  /** The roster as it stood at the last checkered flag. */
  roster: readonly DriverCard[];
  teams: readonly Team[];
  /**
   * The season's money stream. Without it nobody goes broke and crew grades
   * move on performance alone — which is a legal way to run the league, just a
   * gentler one.
   */
  money?: readonly MoneyEvent[];
  /** driverId → the finance team id his money was booked under. */
  financeTeamOf?: Record<string, string>;
  /** How many seasons each driver has run. Absent = rookie (SAG/5). */
  seasonsRun?: Record<string, number>;
  options?: OffseasonOptions;
  /**
   * D-EZ-91 — the books after this season (`SeasonFinanceResult.carry`). When
   * given, D-031's fold test reads each shop's CUMULATIVE bank, as its comment
   * always said it did, and the winter hands the books on in `carry`.
   */
  financeCarry?: FinanceCarry;
  /** D-EZ-97 — careers from before the league, passed through untouched. */
  priors?: Record<string, CareerPrior>;
}

export type DemotionReason =
  | 'below-finish-threshold'
  | 'lowest-in-standings'
  | 'team-closed'
  /** D-EZ-93 — lost on a roll against SAG/5's risk, not a fixed count. */
  | 'licence-lost';

/** D-EZ-93 — a driver who walked away. */
export interface Retirement {
  driverId: string;
  name: string;
  /** His age at the end of the season he just raced. */
  age: number;
  rank: number;
  wins: number;
  /** Seasons in this league, this one included. */
  seasons: number;
  /** The chance he was carrying, 0-1, and the d1000 that came up. */
  chance: number;
  roll: number;
  description: string;
}

/**
 * A driver who failed SAG/5's 75% test, whether or not it cost him his licence.
 * The ones who survived it are the story — "he got away with one" is a season
 * arc, and hiding them would make the winter look tidier than it was.
 */
export interface UnrankedDriver {
  driverId: string;
  name: string;
  rank: number;
  finishPct: number;
  /** Starts plus DNQs — the races he was actually judged on. */
  judgedRaces: number;
  dnqs: number;
  dnfs: number;
  demoted: boolean;
}

export interface Demotion {
  driverId: string;
  name: string;
  reason: DemotionReason;
  rank: number;
  /**
   * races finished / races entered, **as a FRACTION, 0–1**. Null if he never
   * entered one.
   *
   * ⚠ THIS SAID "as a percentage" AND IT WAS WRONG, which cost a real bug: the
   * record book printed thirteen demoted drivers as having finished "0%" and
   * "1%" of their races. Note also that `StatRow.finishPct` in
   * `components/statsModel.ts` is the SAME NAME in the other units (0–100), so
   * a reader who checks one and assumes the other is wrong twice.
   *
   * Every existing caller multiplies by 100 — see the two descriptions built in
   * this file.
   */
  finishPct: number | null;
  description: string;
}

/** One field on one card, with the reason it now says what it says. */
export interface RerateStep {
  field: string;
  from: string;
  to: string;
  ruleCitation: string;
  /** The band, roll, or rate the step read. */
  basis: string;
  changed: boolean;
}

export interface DriverRerate {
  driverId: string;
  name: string;
  rank: number;
  before: DriverCard;
  after: DriverCard;
  steps: RerateStep[];
  /** D-EZ-93 — the winter's step on the age curve, already in `after`. */
  aging?: AgingStep;
  /** One or two sentences a feature writer could print unedited. */
  story: string;
}

export interface TeamFailure {
  teamId: string;
  name: string;
  balanceMb: number;
  /** The charter's new identity, when it was bought rather than closed. */
  replacedBy?: { teamId: string; name: string };
  driverIds: string[];
  description: string;
}

export interface OffseasonResult {
  seasonNumber: number;
  demotions: Demotion[];
  /** D-EZ-93 — drivers who chose to walk away. Not demotions. */
  retirements: Retirement[];
  /**
   * D-EZ-94 / D-EZ-91 — everything the next season inherits besides the
   * roster: contracts, banks, wealth, live feuds, injuries and suspensions.
   */
  carry: CareerCarry;
  /** Everyone below SAG/5's line, demoted or not (D-032). */
  unranked: UnrankedDriver[];
  rerates: DriverRerate[];
  teamFailures: TeamFailure[];
  /** Every driver created to fill a seat. */
  rookies: GeneratedDriver[];
  /** Next season's licensed roster, re-rated and restocked. */
  roster: DriverCard[];
  /** Next season's garage. */
  teams: Team[];
  /** Seasons run, advanced by one for everyone who survived. */
  seasonsRun: Record<string, number>;
  /** Crew grade per team, after the RAF% read. */
  crewGrades: Record<string, PitCrewGrade>;
  events: RuleEvent[];
}

// ---------------------------------------------------------------------------
// The winter
// ---------------------------------------------------------------------------

export function runOffseason(input: OffseasonInput): OffseasonResult {
  const { season, roster, teams } = input;
  const opts = input.options ?? {};
  const demoteLowest = opts.demoteLowest ?? 5;
  const finishThreshold = opts.finishThreshold ?? 0.75;
  const onFailure = opts.onFailure ?? 'rebrand';
  const seasonNumber = opts.seasonNumber ?? 1;
  const insolvencyFloorMb =
    opts.insolvencyFloorMb ?? -(WEEKLY_RACE_COST_MB * SAGLIANO_BASELINE_RACES);

  const dice = new Dice(input.seed ^ 0x0ff5_ea50);
  // D-EZ-93 — the age curve, retirement and licence rolls get their OWN stream,
  // so switching aging on or off cannot repaint a single re-rate die.
  const lifeDice = new Dice((input.seed ^ 0x1fe5_a9e5) >>> 0);
  const licensing = opts.licensing ?? 'aging';
  const events: RuleEvent[] = [];
  const standings = season.finalStandings;
  const statOf = (id: string): DriverSeasonStats | undefined => standings.byDriver[id];

  // Only drivers still licensed at the flag can be re-rated; the season already
  // removed anyone who died. The FINAL roster's card is the one carried — it
  // holds any team a transfer moved him to — but the age comes off whichever
  // card has one.
  const finalCardOf = new Map(season.finalRoster.map((c) => [c.driverId, c]));
  const alive = roster.filter((c) => finalCardOf.has(c.driverId));
  // A rookie signed mid-season to replace a driver who died is licensed too,
  // and keeps his seat into next year — the winter fills only what is empty.
  for (const signing of season.rookies) {
    const card = finalCardOf.get(signing.driverId);
    if (card && !alive.some((c) => c.driverId === card.driverId)) alive.push(card);
  }

  // ---- 1. DEPARTURES (SAG/5, and D-EZ-93) --------------------------------
  const departure =
    licensing === 'fixed'
      ? {
          ...demote({
            alive,
            standings,
            demoteLowest,
            finishThreshold,
            minimumJudged: opts.minimumJudgedRaces ?? 8,
            maxUnranked: opts.maxUnranked ?? 8,
          }),
          retirements: [] as Retirement[],
        }
      : departures({
          alive,
          standings,
          seasonsRun: input.seasonsRun,
          dice: lifeDice,
          finishThreshold,
          minimumJudged: opts.minimumJudgedRaces ?? 8,
        });
  const { demotions, survivors, unranked, retirements } = departure;
  for (const r of retirements) {
    events.push({
      eventType: 'offseason',
      momentTag: 'career.retired',
      ruleCitation: CITE_RETIREMENT,
      actorIds: [r.driverId],
      positionDeltas: [],
      modifiers: [
        { name: 'age', value: r.age },
        { name: 'seasons', value: r.seasons },
        { name: 'final standing', value: r.rank },
        { name: 'chance', value: `${Math.round(r.chance * 1000) / 10}%`, note: `d1000 ${r.roll}` },
      ],
      dice: [],
      description: r.description,
    });
  }
  for (const d of demotions) {
    events.push({
      eventType: 'offseason',
      momentTag: 'career.demoted',
      ruleCitation: CITE_DEMOTION,
      actorIds: [d.driverId],
      positionDeltas: [],
      modifiers: [
        { name: 'final standing', value: d.rank },
        { name: 'races finished', value: d.finishPct === null ? 'none entered' : `${Math.round(d.finishPct * 100)}%` },
      ],
      dice: [],
      description: d.description,
    });
  }

  // ---- 2. THE GARAGE (D-031) ----------------------------------------------
  // Done BEFORE re-rating, because a folded team's crew grade belongs to whoever
  // buys the charter, and a closed charter's drivers are demoted rather than
  // re-rated.
  const garage = settleTheGarage({
    teams,
    money: input.money,
    financeTeamOf: input.financeTeamOf,
    bank: input.financeCarry?.shopBankMb,
    insolvencyFloorMb,
    onFailure,
    seed: input.seed,
    seasonNumber,
  });

  for (const failure of garage.failures) {
    events.push({
      eventType: 'offseason',
      momentTag: failure.replacedBy ? 'team.sold' : 'team.closed',
      ruleCitation: CITE_TEAM_FAILURE,
      actorIds: failure.driverIds,
      positionDeltas: [],
      modifiers: [
        { name: 'balance', value: Math.round(failure.balanceMb), note: 'Megabucks' },
        { name: 'floor', value: Math.round(insolvencyFloorMb) },
      ],
      dice: [],
      description: failure.description,
    });
  }

  // Drivers whose charter closed leave with it.
  const closedSeats = new Set(
    garage.failures.filter((f) => !f.replacedBy).flatMap((f) => f.driverIds),
  );
  const stillHere = survivors.filter((c) => !closedSeats.has(c.driverId));
  for (const driverId of closedSeats) {
    const card = alive.find((c) => c.driverId === driverId);
    if (!card) continue;
    if (demotions.some((d) => d.driverId === driverId)) continue;
    demotions.push({
      driverId,
      name: card.name,
      reason: 'team-closed',
      rank: statOf(driverId)?.rank ?? 0,
      finishPct: finishRate(statOf(driverId)),
      description: `${card.name} is out of a ride — the shop closed its doors and nobody bought the charter.`,
    });
  }

  // ---- 3. PIT CREW, per team (SAG/8 read through D-007) -------------------
  const crewReads =
    (opts.crewGrading ?? 'percentile') === 'percentile'
      ? gradeCrewsByPercentile({ teams: garage.teams, standings, roster })
      : rerateCrews({ teams: garage.teams, standings, roster });
  const crewGrades: Record<string, PitCrewGrade> = {};
  for (const [teamId, read] of Object.entries(crewReads)) {
    crewGrades[teamId] = read.grade;
    if (read.from === read.grade) continue;
    const team = garage.teams.find((t) => t.teamId === teamId);
    events.push({
      eventType: 'offseason',
      momentTag: 'team.crew-regrade',
      ruleCitation: CITE_PIT_CREW,
      actorIds: team?.driverIds ?? [],
      positionDeltas: [],
      modifiers: [{ name: 'crew grade', value: `${read.from} → ${read.grade}`, note: read.explanation }],
      dice: [],
      description: `${team?.name ?? teamId}'s crew is re-graded ${read.from} to ${read.grade} on the shop's finish rate across all its cars.`,
    });
  }

  // ---- 4. RE-RATING (SAG/6-8) ---------------------------------------------
  const context = buildRerateContext({
    standings,
    roster: stillHere,
    money: input.money,
    escapeNorm: opts.escapeNorm ?? 'league',
  });
  const rerates: DriverRerate[] = [];
  const rerated: DriverCard[] = [];

  // Stable order — by final standing, then id — so the dice stream is reproducible.
  const ordered = [...stillHere].sort(
    (a, b) => (statOf(a.driverId)?.rank ?? 9999) - (statOf(b.driverId)?.rank ?? 9999) || a.driverId.localeCompare(b.driverId),
  );

  for (const card of ordered) {
    const stat = statOf(card.driverId);
    if (!stat) {
      rerated.push(card);
      continue;
    }
    const teamId = garage.teamOfDriver[card.driverId] ?? card.teamId;
    const result = rerateDriver({
      card,
      stat,
      dice,
      context,
      crewGrade: teamId ? crewGrades[teamId] ?? card.pitCrewGrade : (crewGrades[card.driverId] ?? card.pitCrewGrade),
      teamId,
      pedalStepCap: opts.pedalStepCap === undefined ? 1 : opts.pedalStepCap,
      brakeSource: opts.brakeSource ?? 'published',
    });
    // D-EZ-93 — a year older, and a step along the curve. The re-rate above is
    // what the season earned; this is what the calendar does on top of it.
    if (licensing === 'aging') applyAging(result, lifeDice);
    rerates.push(result);
    rerated.push(result.after);
    events.push({
      eventType: 'offseason',
      momentTag: 'career.rerated',
      ruleCitation: CITE_PEDAL_TOTAL,
      actorIds: [card.driverId],
      positionDeltas: [],
      modifiers: result.steps
        .filter((s) => s.changed)
        .map((s) => ({ name: s.field, value: `${s.from} → ${s.to}`, note: s.basis })),
      dice: [],
      description: result.story,
    });
  }

  // A driver nobody could re-rate (he never turned up) still gets a year older.
  if (licensing === 'aging') {
    for (let i = 0; i < rerated.length; i++) {
      if (rerates.some((r) => r.driverId === rerated[i].driverId)) continue;
      rerated[i] = { ...rerated[i], age: ageOf(rerated[i]) + 1 };
    }
  }

  // ---- 5. ROOKIES (SAG/5) -------------------------------------------------
  const vacancies = roster.length - rerated.length;
  const { rookies, cards } = signRookies({
    count: Math.max(0, vacancies),
    dice,
    existing: rerated,
    seasonNumber,
    withAge: licensing === 'aging',
  });
  for (const rookie of rookies) {
    events.push({
      eventType: 'offseason',
      momentTag: 'career.rookie',
      ruleCitation: CITE_ROOKIE_TABLES,
      actorIds: [rookie.card.driverId],
      positionDeltas: [],
      modifiers: [
        { name: 'qualities', value: rookie.card.qualities.length },
        { name: 'gas', value: rookie.card.gasPedal },
        { name: 'brake', value: rookie.card.brakePedal },
      ],
      dice: [],
      description: `${rookie.card.name} is licensed for next season, rolled on the inaugural tables like every rookie before him.`,
    });
  }

  // ---- 6. NEXT SEASON'S GARAGE -------------------------------------------
  const nextRoster = [...rerated, ...cards];
  const nextTeams = seatTheGarage({
    teams: garage.teams,
    roster: nextRoster,
    crewGrades,
  });

  // A driver who RACED this season has completed one, whether or not anybody was
  // counting before now. The old line read `previous === undefined ? 0 : ...`,
  // which handed the entire inaugural roster a 0 after their first season —
  // indistinguishable from a rookie signed that same winter who had never turned
  // a lap. Nothing noticed until EZRA traits needed to know who the first-year
  // rookies actually were, and the audit started calling founding veterans
  // rookies. New signings still start at 0; they have not raced yet.
  const raced = new Set(rerated.map((c) => c.driverId));
  const seasonsRun: Record<string, number> = {};
  for (const card of nextRoster) {
    const previous = input.seasonsRun?.[card.driverId];
    seasonsRun[card.driverId] =
      previous !== undefined ? previous + 1 : raced.has(card.driverId) ? 1 : 0;
  }

  // ---- 7. EZRA DRIVER TRAITS (EZRA-S2 §4.0) -------------------------------
  // Drivers who have now finished a season stop being first-year rookies and
  // become eligible; the league is topped back up to its share. Without this the
  // trait population drains away as veterans are replaced by rookies who can
  // never earn one — super-speedway drafting would quietly stop happening over a
  // long career, and nothing would report it. Traits already held are untouched.
  const rookieIds = Object.entries(seasonsRun)
    .filter(([, seasons]) => seasons < 1)
    .map(([driverId]) => driverId);
  const newTraits = topUpTraits((input.seed ^ 0x71a17500) >>> 0, nextTeams.roster, {
    share: opts.traitShare,
    rookieIds,
  });
  const withTraits = applyTraits(nextTeams.roster, newTraits);
  for (const [driverId, code] of newTraits.byDriver) {
    const def = TRAITS_BY_CODE[code];
    const name = withTraits.find((c) => c.driverId === driverId)?.name ?? driverId;
    events.push({
      eventType: 'offseason',
      momentTag: 'career.trait-awarded',
      ruleCitation: newTraits.ruleCitation,
      actorIds: [driverId],
      positionDeltas: [],
      modifiers: [
        { name: 'trait', value: code, note: def.name },
        { name: 'seasons run', value: seasonsRun[driverId] ?? 0 },
      ],
      dice: [],
      description:
        `${name} is credited with ${def.name} [${code}] for next season — his rookie year is behind ` +
        `him, and the garage has seen enough to put it on the card.`,
    });
  }

  // ---- 8. WHAT THE NEXT SPRING INHERITS (D-EZ-91, D-EZ-94) --------------
  const nextIds = new Set(withTraits.map((c) => c.driverId));
  const carry = buildCarry({
    season,
    financeCarry: input.financeCarry,
    failures: garage.failures,
    nextIds,
    dice: lifeDice,
    priors: input.priors,
    events,
    nameOf: (id) =>
      withTraits.find((c) => c.driverId === id)?.name ?? standings.byDriver[id]?.name ?? id,
  });

  return {
    seasonNumber,
    demotions,
    retirements,
    carry,
    unranked,
    rerates,
    teamFailures: garage.failures,
    rookies,
    roster: withTraits,
    teams: nextTeams.teams,
    seasonsRun,
    crewGrades,
    events,
  };
}

// ---------------------------------------------------------------------------
// 1. Demotion (SAG/5)
// ---------------------------------------------------------------------------

/**
 * SAG/5's denominator, and it is not `entries`.
 *
 * "Drivers must finish 75% of their races to be ranked at the end of the
 * season. This includes DNF and DNQ drivers."
 *
 * A DNF counts against him. A DNQ counts against him — he was in the session
 * and was not fast enough. But our league licenses 53 drivers for 43 seats
 * (D-024), which Sagliano's does not: his is 43 cars for 43 slots and everyone
 * who turns up races. So we have a category he never had — the driver the FIELD
 * DID NOT NEED, left on the bubble after STEP SIX took its cut, who failed at
 * nothing.
 *
 * Counting those weeks against him is catastrophic and was measured as such:
 * with `entries` as the denominator, 31 of 53 drivers were demoted every single
 * winter — sixty per cent of the league, most of them for the crime of being
 * on a 53-man entry list. Denominator is therefore STARTS PLUS DNQs: every
 * weekend he was actually judged on.
 */
export function finishRate(stat: DriverSeasonStats | undefined): number | null {
  if (!stat) return null;
  const judged = stat.starts + stat.dnqs;
  if (judged === 0) return null;
  return stat.racesFinished / judged;
}

/**
 * D-032 — THE 75% RULE, AND WHY IT CANNOT BE APPLIED WHOLESALE.
 *
 * SAG/5, verbatim: "Demote five experienced drivers that have placed lowest in
 * the standings in addition to the drivers who failed to finish 75% of their
 * races due to DNQ and/or DNF… If a driver does not qualify for a ranking at
 * the end of a season, he will be demoted before other experienced ranked
 * drivers on the list. In that case, more than five rookie drivers will need to
 * be created."
 *
 * Read literally that demotes everybody under the line. MEASURED against a real
 * season of this engine, that is **31 of 53 drivers** — sixty per cent of the
 * league, every single winter, replaced by rookies. Six seasons of it and the
 * league has no history at all, which is the opposite of what the section is
 * for ("over time drivers improve or decline and suddenly (or GRADUALLY) are
 * demoted out of the league").
 *
 * WHY THE NUMBER IS SO HIGH, precisely:
 *
 *   • This engine retires 8.8 cars from a 43-car race, so a driver who starts
 *     every race still averages seven or eight DNFs a season — right on the
 *     line before a single DNQ.
 *   • And we license 53 drivers for 43 seats (D-024), which Sagliano's league
 *     does not. His is 43 for 43: nobody is left on the bubble, and a DNQ is a
 *     rare Race Week event rather than a weekly fact of life. Drivers ranked
 *     26th and 29th in the measured season carried RAF% of 92 and 88 — good
 *     seasons — and still failed the threshold on DNQs alone.
 *
 * So his threshold and our field size are incompatible, and the incompatibility
 * is ours, not his.
 *
 * RULING: the rule stands exactly as printed, and its ORDERING stands — the
 * unranked are demoted BEFORE any ranked driver, and more than five rookies are
 * created. What is capped is how many of them lose their licence in one winter:
 * `maxUnranked`, worst finish rate first, default eight. Together with the five
 * lowest-ranked that is thirteen of fifty-three — a quarter of the league,
 * which is real churn and still leaves a league with a memory.
 *
 * Every driver who failed the threshold is returned in `unranked` whether or
 * not he was demoted, so the interface can say "thirty-one drivers finished
 * below the line; eight lost their licence" — which is both more honest and
 * better television than hiding the survivors.
 */
function demote(args: {
  alive: readonly DriverCard[];
  standings: SeasonStandings;
  demoteLowest: number;
  finishThreshold: number;
  minimumJudged: number;
  maxUnranked: number;
}): { demotions: Demotion[]; survivors: DriverCard[]; unranked: UnrankedDriver[] } {
  const { alive, standings, demoteLowest, finishThreshold, minimumJudged, maxUnranked } = args;
  const demotions: Demotion[] = [];
  const gone = new Set<string>();
  const unranked: UnrankedDriver[] = [];

  /**
   * SAG/5, and the sentence matters: "Drivers must finish 75% of their races to
   * be ranked at the end of the season. This includes DNF and DNQ drivers."
   *
   * So the denominator is RACES ENTERED — every weekend he turned up for —
   * and a DNQ counts against him exactly as a DNF does. That is stricter than
   * RAF% (which the pit-crew table uses) only in that it is applied as a hard
   * cut rather than a rating.
   */
  for (const card of alive) {
    const stat = standings.byDriver[card.driverId];
    const rate = finishRate(stat);
    if (rate === null || rate >= finishThreshold) continue;
    // D-008's principle, applied to the demotion cut rather than to a rating:
    // a hard career-ending threshold read off three starts is noise, not
    // evidence. A driver who never got enough of a look is carried, and takes
    // his chances in the five-lowest cut like everybody else.
    if (stat && stat.starts + stat.dnqs < minimumJudged) continue;
    unranked.push({
      driverId: card.driverId,
      name: card.name,
      rank: stat?.rank ?? 0,
      finishPct: rate,
      judgedRaces: stat ? stat.starts + stat.dnqs : 0,
      dnqs: stat?.dnqs ?? 0,
      dnfs: stat?.dnfs ?? 0,
      demoted: false,
    });
  }

  // Worst first — a career ends on the number, not on the alphabet.
  unranked.sort((a, b) => a.finishPct - b.finishPct || a.driverId.localeCompare(b.driverId));
  for (const entry of unranked.slice(0, Math.max(0, maxUnranked))) {
    entry.demoted = true;
    gone.add(entry.driverId);
    demotions.push({
      driverId: entry.driverId,
      name: entry.name,
      reason: 'below-finish-threshold',
      rank: entry.rank,
      finishPct: entry.finishPct,
      description: `${entry.name} finished only ${Math.round(entry.finishPct * 100)}% of the ${entry.judgedRaces} races he was judged on — ${entry.dnfs} wrecked or broken, ${entry.dnqs} not fast enough to make the show. He does not qualify for a ranking and loses his licence before anyone is demoted on points.`,
    });
  }

  // "in addition to" — the five lowest RANKED drivers, chosen from those who
  // are still here after the unranked have gone.
  const ranked = standings.drivers
    .filter((d) => !gone.has(d.driverId) && alive.some((c) => c.driverId === d.driverId))
    .sort((a, b) => b.rank - a.rank);

  for (const stat of ranked.slice(0, Math.max(0, demoteLowest))) {
    gone.add(stat.driverId);
    demotions.push({
      driverId: stat.driverId,
      name: stat.name,
      reason: 'lowest-in-standings',
      rank: stat.rank,
      finishPct: finishRate(stat),
      description: `${stat.name} finished ${ordinal(stat.rank)} and is demoted out of the league — one of the five lowest-placed experienced drivers.`,
    });
  }

  return { demotions, survivors: alive.filter((c) => !gone.has(c.driverId)), unranked };
}

/**
 * D-EZ-93 — HOW CAREERS END NOW.
 *
 * Every licensed driver, best finisher first, faces two rolls on the winter's
 * life stream:
 *
 *   1. RETIREMENT — `retirementChance(age, season)`: rising with age, cut by
 *      winning, raised by a season at the back. A d1000 under it and he walks.
 *   2. LICENCE LOSS — only if he stayed: SAG/5's two tests (placed at the
 *      bottom of the ranked field; failed the 75% finishing rule on a fair
 *      sample) mark him AT RISK, and `licenceLossChance` is rolled. There is no
 *      count to hit. A kind winter can cost two licences and a hard one eight.
 *
 * The 75% rule is still reported in full (`unranked`), demoted or not, so the
 * winter can say "eleven finished below the line; three lost their licence".
 */
function departures(args: {
  alive: readonly DriverCard[];
  standings: SeasonStandings;
  seasonsRun?: Record<string, number>;
  dice: Dice;
  finishThreshold: number;
  minimumJudged: number;
}): { demotions: Demotion[]; survivors: DriverCard[]; unranked: UnrankedDriver[]; retirements: Retirement[] } {
  const { alive, standings, dice, finishThreshold, minimumJudged } = args;
  const fieldSize = standings.drivers.length;
  const ordered = [...alive].sort(
    (a, b) =>
      (standings.byDriver[a.driverId]?.rank ?? 9999) - (standings.byDriver[b.driverId]?.rank ?? 9999) ||
      a.driverId.localeCompare(b.driverId),
  );

  const demotions: Demotion[] = [];
  const retirements: Retirement[] = [];
  const unranked: UnrankedDriver[] = [];
  const gone = new Set<string>();

  for (const card of ordered) {
    const stat = standings.byDriver[card.driverId];
    const age = ageOf(card);
    const seasons = (args.seasonsRun?.[card.driverId] ?? 0) + 1;
    const rate = finishRate(stat);
    const judged = stat ? stat.starts + stat.dnqs : 0;
    const isUnranked = rate !== null && rate < finishThreshold && judged >= minimumJudged;
    const standing: CareerStanding = {
      rank: stat?.rank ?? fieldSize + 1,
      wins: stat?.wins ?? 0,
      seasons,
      unranked: isUnranked,
      fieldSize,
    };
    let entry: UnrankedDriver | null = null;
    if (isUnranked && rate !== null) {
      entry = {
        driverId: card.driverId,
        name: card.name,
        rank: standing.rank,
        finishPct: rate,
        judgedRaces: judged,
        dnqs: stat?.dnqs ?? 0,
        dnfs: stat?.dnfs ?? 0,
        demoted: false,
      };
      unranked.push(entry);
    }

    // 1. Does he walk away? (Rolled for everyone, so the stream is fixed.)
    const retireP = retirementChance(age, standing);
    const retireRoll = dice.d(1000);
    if (retireRoll <= Math.round(retireP * 1000)) {
      gone.add(card.driverId);
      retirements.push({
        driverId: card.driverId,
        name: card.name,
        age,
        rank: standing.rank,
        wins: standing.wins,
        seasons,
        chance: retireP,
        roll: retireRoll,
        description:
          standing.rank === 1
            ? `${card.name} goes out on top — champion at ${age}, and he hangs up the helmet after ${seasons} season${seasons === 1 ? '' : 's'}.`
            : standing.wins > 0
              ? `${card.name} retires at ${age} after ${seasons} season${seasons === 1 ? '' : 's'}, a race winner to the last (${standing.wins} this year).`
              : `${card.name} retires at ${age} after ${seasons} season${seasons === 1 ? '' : 's'}${standing.rank <= fieldSize ? `, ${ordinal(standing.rank)} in his final year` : ''}.`,
      });
      continue;
    }

    // 2. Does the league keep him? (Only rolled when SAG/5 puts him at risk.)
    const loseP = licenceLossChance(age, standing);
    if (loseP <= 0) continue;
    const loseRoll = dice.d(1000);
    if (loseRoll > Math.round(loseP * 1000)) continue;
    gone.add(card.driverId);
    if (entry) entry.demoted = true;
    demotions.push({
      driverId: card.driverId,
      name: card.name,
      reason: 'licence-lost',
      rank: standing.rank,
      finishPct: rate,
      description: isUnranked
        ? `${card.name} finished only ${Math.round((rate ?? 0) * 100)}% of the ${judged} races he was judged on and ${ordinal(standing.rank)} in the standings. Nobody renews his licence.`
        : `${card.name} finished ${ordinal(standing.rank)}, down among the cars that do not make a full field, and loses his licence.`,
    });
  }

  return { demotions, survivors: alive.filter((c) => !gone.has(c.driverId)), unranked, retirements };
}

/**
 * D-EZ-92 — CREW GRADES BY RANK, NOT BY A NUMBER THAT SLIDES.
 *
 * SAG/8 reads a crew's grade straight off its RAF% (races finished / entered).
 * But RAF% is mostly a measure of how much wrecking the league does: when DNFs
 * rose, every crew's RAF% fell with them and the league went from 7 A-grade
 * crews to 1 and from 10 E-grade to 36 in ten winters (audit, seed 77) — which
 * cost more repairs, which cost more DNFs. Graded by RANK the league keeps its
 * shape: the best finish rate in the garage is an A whatever the year was like.
 *
 * Every crew — each chartered shop, pooled over its cars, and each independent
 * on his own — is ranked by finish rate. Grades are handed out down that order
 * in the shares the inaugural tables produce (`CREW_GRADE_SHARES`, measured
 * over 300 founding fields), weighted by cars, so a three-car shop takes three
 * cars' worth of the A band.
 *
 * HOW FAR A CREW MAY MOVE is D-008's evidence rule, the same tiers
 * `evidenceWeightedRerate` uses: fewer than 12 starts (PIT CREW's printed full
 * sample) does not move it at all; 12-35 starts, at most two grades; a full
 * season's worth (36+ starts — every shop and most independents) goes straight
 * to its ranked grade, because a full sample is trusted outright.
 *
 * ⚠ IT USED TO BE ONE GRADE A WINTER, AND THAT WAS WRONG (found on the
 * integrated branch, seed 77, 4 Oct 2026). Founding shops carry three cards
 * rolled independently — "AED", "DAB" — so a shop's starting grade was simply
 * its first card's letter, and the best-ranked crews of season one mostly
 * started at D or E. Held to one step, they reached C, nobody reached A, and
 * the league went from 7 A-grade cards to 1 in a single winter. The prior is
 * now the shop's MEDIAN card, and full evidence is not capped.
 */
export const CREW_GRADE_SHARES: readonly { grade: PitCrewGrade; share: number }[] = [
  { grade: 'A', share: 0.184 },
  { grade: 'B', share: 0.138 },
  { grade: 'C', share: 0.138 },
  { grade: 'D', share: 0.337 },
  { grade: 'E', share: 0.203 },
];

const GRADE_ORDER: readonly PitCrewGrade[] = ['A', 'B', 'C', 'D', 'E'];
/** A season's starts: enough evidence to grade a crew outright (D-008). */
const CREW_FULL_SEASON_STARTS = 36;

/** The grade a crew at this point of the ranking (0 = best, 1 = worst) is owed. */
export function gradeForPercentile(p: number): PitCrewGrade {
  let cum = 0;
  for (const band of CREW_GRADE_SHARES) {
    cum += band.share;
    if (p < cum) return band.grade;
  }
  return 'E';
}

function gradeCrewsByPercentile(args: {
  teams: readonly Team[];
  standings: SeasonStandings;
  roster: readonly DriverCard[];
}): Record<string, { grade: PitCrewGrade; from: PitCrewGrade; explanation: string }> {
  const cardOf = new Map(args.roster.map((c) => [c.driverId, c]));
  const charterOf = new Map<string, string>();
  for (const t of args.teams) for (const id of t.driverIds) charterOf.set(id, t.teamId);

  type Unit = { id: string; cars: number; finished: number; entered: number; current: PitCrewGrade };
  const units: Unit[] = [];
  for (const team of args.teams) {
    let finished = 0;
    let entered = 0;
    for (const id of team.driverIds) {
      const stat = args.standings.byDriver[id];
      if (!stat) continue;
      finished += stat.racesFinished;
      entered += stat.starts;
    }
    // The shop's crew as it stands: the MEDIAN of its cards. After one winter
    // every card in a shop prints the same letter; a founding shop's three
    // were rolled independently, and its first card is no better a summary
    // than its last.
    const grades = team.driverIds
      .map((id) => cardOf.get(id)?.pitCrewGrade)
      .filter((g): g is PitCrewGrade => Boolean(g))
      .map((g) => GRADE_ORDER.indexOf(g))
      .sort((a, b) => a - b);
    const current = grades.length > 0 ? GRADE_ORDER[grades[Math.floor((grades.length - 1) / 2)]] : 'C';
    units.push({ id: team.teamId, cars: Math.max(1, team.driverIds.length), finished, entered, current });
  }
  for (const card of args.roster) {
    if (charterOf.has(card.driverId)) continue;
    const stat = args.standings.byDriver[card.driverId];
    units.push({
      id: card.driverId,
      cars: 1,
      finished: stat?.racesFinished ?? 0,
      entered: stat?.starts ?? 0,
      current: card.pitCrewGrade,
    });
  }

  const rate = (u: Unit): number => (u.entered > 0 ? u.finished / u.entered : -1);
  const ranked = [...units].sort(
    (a, b) =>
      rate(b) - rate(a) ||
      GRADE_ORDER.indexOf(a.current) - GRADE_ORDER.indexOf(b.current) ||
      a.id.localeCompare(b.id),
  );
  const totalCars = ranked.reduce((n, u) => n + u.cars, 0) || 1;

  const out: Record<string, { grade: PitCrewGrade; from: PitCrewGrade; explanation: string }> = {};
  let before = 0;
  ranked.forEach((u, i) => {
    const mid = (before + u.cars / 2) / totalCars;
    before += u.cars;
    const target = gradeForPercentile(mid);
    const pct = u.entered > 0 ? Math.round((u.finished / u.entered) * 100) : null;
    if (u.entered < 12) {
      out[u.id] = {
        grade: u.current,
        from: u.current,
        explanation: `only ${u.entered} starts — too few to re-grade the crew (D-008)`,
      };
      return;
    }
    const from = GRADE_ORDER.indexOf(u.current);
    const to = GRADE_ORDER.indexOf(target);
    const maxSteps = u.entered >= CREW_FULL_SEASON_STARTS ? 4 : 2;
    const grade = Math.abs(to - from) <= maxSteps ? target : GRADE_ORDER[from + Math.sign(to - from) * maxSteps];
    out[u.id] = {
      grade,
      from: u.current,
      explanation:
        `${pct}% of starts finished, ${ordinal(i + 1)}-best crew of ${ranked.length} — a grade-${target} finish rate this year` +
        (grade !== target ? `; only ${u.entered} starts to judge on, so ${grade} for now` : ''),
    };
  });
  return out;
}

/**
 * D-EZ-94 / D-EZ-91 — WHAT THE NEXT SPRING INHERITS.
 *
 * FEUDS cool over the winter. Each one still live at the last checkered flag
 * rolls a d6 on the life stream: 1-3 it cools off for good, 4-6 it is still
 * there on opening day — the same even odds the weekly RB/xvi roll gives a
 * grudge, so a feud that carries is one more week of a feud, not a promise.
 * INJURIES and SUSPENSIONS with races left are served next season. Anything
 * involving a driver who is not licensed next year is dropped.
 *
 * THE BOOKS: a folded shop's bank goes with it — a bought charter starts on
 * clean books under its new id (D-031); a closed one is gone.
 */
function buildCarry(args: {
  season: SeasonResult;
  financeCarry?: FinanceCarry;
  failures: readonly TeamFailure[];
  nextIds: ReadonlySet<string>;
  dice: Dice;
  priors?: Record<string, CareerPrior>;
  events: RuleEvent[];
  nameOf: (id: string) => string;
}): CareerCarry {
  const out = emptyCareerCarry(args.priors);
  const end = args.season.carryOut;

  if (end) {
    for (const feud of end.feuds) {
      if (!args.nextIds.has(feud.a) || !args.nextIds.has(feud.b)) continue;
      const d6 = args.dice.d6();
      const carried = d6 >= 4;
      if (carried) out.season.feuds.push({ a: feud.a, b: feud.b });
      args.events.push({
        eventType: 'offseason',
        momentTag: carried ? 'career.feud-carried' : 'career.feud-cooled',
        ruleCitation: CITE_FEUD_WINTER,
        actorIds: [feud.a, feud.b],
        positionDeltas: [],
        modifiers: [{ name: 'winter d6', value: d6, note: '1-3 cools off, 4-6 carries' }],
        dice: [{ kind: 'd6', dice: [d6], label: 'feud over the winter' }],
        description: carried
          ? `${args.nameOf(feud.a)} and ${args.nameOf(feud.b)} have not forgotten. The feud carries into next season.`
          : `A winter apart cools ${args.nameOf(feud.a)} and ${args.nameOf(feud.b)} off.`,
      });
    }
    out.season.suspended = end.suspended.filter(([id, n]) => args.nextIds.has(id) && n > 0);
    out.season.injured = end.injured.filter(([id, n]) => args.nextIds.has(id) && n > 0);
  }

  if (args.financeCarry) {
    const bank = { ...args.financeCarry.shopBankMb };
    for (const f of args.failures) {
      delete bank[f.teamId];
      if (f.replacedBy) bank[f.replacedBy.teamId] = 0;
    }
    out.finance = {
      contracts: Object.fromEntries(
        Object.entries(args.financeCarry.contracts).filter(([id]) => args.nextIds.has(id)),
      ),
      shopBankMb: bank,
      driverWealthMb: { ...args.financeCarry.driverWealthMb },
      winters: args.financeCarry.winters,
    };
  }
  return out;
}

// ---------------------------------------------------------------------------
// 2. The garage (D-031)
// ---------------------------------------------------------------------------

/**
 * D-031 — TEAM FAILURE AND THE CHARTER.
 *
 * Sagliano has no insolvency rule, because he has no persistent team balance
 * for one to act on: SAG/13 says "Maintain the Team budget on a per race basis"
 * and never carries anything forward. Once money persists across a career — the
 * whole point of the finance ledger — a rule is unavoidable, so this one is
 * ours and is labelled as such.
 *
 * A team folds when its CUMULATIVE balance is below one season's fixed cost,
 * i.e. it has lost more than it costs to go racing for a year and cannot fund
 * the next one. Not "ended the year negative": a bad season should hurt, and a
 * team should die from sustained failure over several of them. At D-030's
 * economy roughly two fifths of teams end a season in the red and almost none
 * of them are anywhere near this floor in year one.
 *
 * What happens next is the interesting part, and it is the answer to the
 * obvious question — **yes, a new fictional team is created.** The CHARTER
 * survives and is bought: same seats, same cars, new name, new colours, new
 * owner, and books reset to zero. That is what actually happens when a race
 * team fails, it keeps the field the size it was, and it means a driver is not
 * punished for his owner's accounting. The alternative — closing the charter
 * and demoting its drivers — is available as `onFailure: 'close'`.
 */
function settleTheGarage(args: {
  teams: readonly Team[];
  money?: readonly MoneyEvent[];
  financeTeamOf?: Record<string, string>;
  /** D-EZ-91 — each shop's cumulative bank, this season included. */
  bank?: Readonly<Record<string, number>>;
  insolvencyFloorMb: number;
  onFailure: 'rebrand' | 'close';
  seed: number;
  seasonNumber: number;
}): { teams: Team[]; failures: TeamFailure[]; teamOfDriver: Record<string, string> } {
  const { teams, money, financeTeamOf, bank, insolvencyFloorMb, onFailure, seed, seasonNumber } = args;

  const failures: TeamFailure[] = [];
  const out: Team[] = [];
  const teamOfDriver: Record<string, string> = {};

  if (!bank && (!money || money.length === 0)) {
    for (const team of teams) {
      out.push(team);
      for (const id of team.driverIds) teamOfDriver[id] = team.teamId;
    }
    return { teams: out, failures, teamOfDriver };
  }

  const ledger = foldMoney(money ?? []);
  const balanceOfShop = (team: Team): number => {
    // D-EZ-91 — THE CUMULATIVE BANK, as this rule's own header always said.
    // Before, only the season just run was read, and a shop could lose money
    // for ten straight years without ever being judged on it.
    if (bank) return bank[team.teamId] ?? 0;
    // Each driver keeps his own books (SAG/12, D-029). A shop's health is the
    // sum of its cars, because that is what the owner is actually holding —
    // even though he may not move money between them.
    let total = 0;
    for (const driverId of team.driverIds) {
      const financeId = financeTeamOf?.[driverId] ?? `car-${driverId}`;
      total += ledger.books[`team:${financeId}`]?.balanceMb ?? 0;
    }
    return total;
  };

  const usedNames = new Set(teams.map((t) => t.name));
  const rng = makeSeededPick(seed ^ 0xbadb_00c);

  for (const team of teams) {
    const balanceMb = balanceOfShop(team);
    if (balanceMb >= insolvencyFloorMb) {
      out.push(team);
      for (const id of team.driverIds) teamOfDriver[id] = team.teamId;
      continue;
    }

    if (onFailure === 'close') {
      failures.push({
        teamId: team.teamId,
        name: team.name,
        balanceMb,
        driverIds: [...team.driverIds],
        description: `${team.name} is finished — ${Math.round(-balanceMb)} Mb in the hole with nobody willing to take it on. The charter is retired and its ${team.driverIds.length} cars with it.`,
      });
      continue;
    }

    const name = newTeamName(rng, usedNames);
    usedNames.add(name);
    const teamId = `${team.teamId.replace(/-s\d+$/, '')}-s${seasonNumber + 1}`;
    const bought: Team = {
      teamId,
      name,
      driverIds: [...team.driverIds],
      baseInk: team.baseInk,
      livery: liveryFor(teamId, undefined, team.baseInk),
    };
    out.push(bought);
    for (const id of bought.driverIds) teamOfDriver[id] = teamId;

    failures.push({
      teamId: team.teamId,
      name: team.name,
      balanceMb,
      replacedBy: { teamId, name },
      driverIds: [...team.driverIds],
      description: `${team.name} went ${Math.round(-balanceMb)} Mb under and could not fund another season. The charter has been bought and runs next year as ${name} — same cars, new paint, clean books.`,
    });
  }

  // The bought charters get fresh colours, dealt across the whole garage so no
  // two shops end up the same. Repainting only the new ones would let a buyer
  // land on a colour somebody else is already running.
  const inks = assignInks(out.map((t) => t.teamId));
  const painted = out.map((team, i) =>
    team.baseInk === inks[i] ? team : { ...team, baseInk: inks[i], livery: liveryFor(team.teamId, undefined, inks[i]) },
  );

  return { teams: painted, failures, teamOfDriver };
}

// ---------------------------------------------------------------------------
// 3. Pit crew, per team (SAG/8 through D-007)
// ---------------------------------------------------------------------------

/**
 * D-007 — crew grade is a TEAM attribute, so it is read off the shop's
 * aggregate finish rate rather than one driver's. Evidence-weighted per D-008,
 * with the sample being every race every one of the shop's cars entered — which
 * is why a three-car team reaches a full sample three times as fast as an
 * independent, and correctly so.
 */
function rerateCrews(args: {
  teams: readonly Team[];
  standings: SeasonStandings;
  roster: readonly DriverCard[];
}): Record<string, { grade: PitCrewGrade; from: PitCrewGrade; explanation: string }> {
  const out: Record<string, { grade: PitCrewGrade; from: PitCrewGrade; explanation: string }> = {};
  const cardOf = new Map(args.roster.map((c) => [c.driverId, c]));

  for (const team of args.teams) {
    let finished = 0;
    let entered = 0;
    for (const driverId of team.driverIds) {
      const stat = args.standings.byDriver[driverId];
      if (!stat) continue;
      finished += stat.racesFinished;
      entered += stat.entries;
    }

    // The grade the shop actually carries, read off any of its cards — they all
    // print the same letter because it belongs to the crew, not the driver
    // (D-007). This is the PRIOR the evidence is weighted against, and getting
    // it wrong would quietly drag every crew in the league toward the same
    // middle grade every winter.
    const current =
      team.driverIds.map((id) => cardOf.get(id)?.pitCrewGrade).find((g): g is PitCrewGrade => Boolean(g)) ?? 'C';

    const result = evidenceWeightedRerate<PitCrewGrade>({
      table: PIT_CREW_TABLE,
      successes: finished,
      attempts: entered,
      currentRating: current,
    });
    out[team.teamId] = { grade: result.rating, from: current, explanation: result.explanation };
  }
  return out;
}

// ---------------------------------------------------------------------------
// 4. Re-rating one driver (SAG/6-8)
// ---------------------------------------------------------------------------

interface RerateContext {
  /** driverId → 1-based rank on average start, DNQ drivers excluded entirely. */
  startRank: Map<string, number>;
  /** Drivers in the top quarter by aggression — SAG/7's HEAT rule. */
  heatSet: Set<string>;
  /** The single most aggressive driver — SAG/7's starred HEAT. */
  heatKing: string | null;
  /** SAG/7's starred STAR: the champion, the biggest earner, the biggest brand. */
  starredStar: Set<string>;
  raceWinners: Set<string>;
  /** D-EZ-92 — percentage points added to every escape rate (0 = read in a vacuum). */
  escapeShift: number;
}

function buildRerateContext(args: {
  standings: SeasonStandings;
  roster: readonly DriverCard[];
  money?: readonly MoneyEvent[];
  escapeNorm?: 'league' | 'absolute';
}): RerateContext {
  const { standings, roster, money } = args;
  const ids = new Set(roster.map((c) => c.driverId));
  const stats = standings.drivers.filter((d) => ids.has(d.driverId));

  // SAG/8 — "If one or more DNQ, there is no trophy for the driver." Excluded
  // from the ranking rather than sorted to the bottom of it, so the ten gold
  // trophies go to the ten fastest qualifiers who never missed a show.
  const eligible = stats.filter((d) => d.dnqs === 0 && d.avgStart !== null);
  eligible.sort((a, b) => (a.avgStart as number) - (b.avgStart as number) || a.driverId.localeCompare(b.driverId));
  const startRank = new Map(eligible.map((d, i) => [d.driverId, i + 1]));

  // SAG/7 — "give HEAT to top 25% of drivers who had the most instances of
  // initiating a duel, being engaged in a fight, in an altercation, etc."
  const byHeat = [...stats].sort(
    (a, b) => b.heatIncidents - a.heatIncidents || a.driverId.localeCompare(b.driverId),
  );
  const heatCut = Math.max(1, Math.round(byHeat.length * 0.25));
  const heatSet = new Set(byHeat.slice(0, heatCut).filter((d) => d.heatIncidents > 0).map((d) => d.driverId));
  const heatKing = byHeat[0] && byHeat[0].heatIncidents > 0 ? byHeat[0].driverId : null;

  // SAG/7 — starred STAR to the champion, plus the biggest earner and the
  // biggest merchandise brand. "A driver can only have one starred STAR", so
  // this is a set and the three claims collapse where they overlap.
  const starredStar = new Set<string>();
  const champion = stats.find((d) => d.rank === 1);
  if (champion) starredStar.add(champion.driverId);
  if (money) {
    const ledger = foldMoney(money);
    let bestWinnings: { id: string; mb: number } | null = null;
    let bestRoyalties: { id: string; mb: number } | null = null;
    for (const book of Object.values(ledger.books)) {
      if (book.account.kind !== 'driver' || !ids.has(book.account.id)) continue;
      const winnings = book.careerWinningsMb;
      const royalties = book.byCategory.royalties ?? 0;
      if (!bestWinnings || winnings > bestWinnings.mb) bestWinnings = { id: book.account.id, mb: winnings };
      if (!bestRoyalties || royalties > bestRoyalties.mb) bestRoyalties = { id: book.account.id, mb: royalties };
    }
    if (bestWinnings && bestWinnings.mb > 0) starredStar.add(bestWinnings.id);
    if (bestRoyalties && bestRoyalties.mb > 0) starredStar.add(bestRoyalties.id);
  }

  // D-EZ-92 — the league's own escape norm, read over everyone being re-rated.
  const cardOf = new Map(roster.map((c) => [c.driverId, c]));
  const escapeShift =
    (args.escapeNorm ?? 'league') === 'league'
      ? calibratedNormShift(
          ESCAPE_TABLE,
          stats.map((d) => ({
            successes: d.troubleEscapes,
            attempts: d.troubleAttempts,
            currentRating: cardOf.get(d.driverId)?.escape ?? 0,
          })),
        )
      : 0;

  return {
    startRank,
    heatSet,
    heatKing,
    starredStar,
    raceWinners: new Set(stats.filter((d) => d.wins > 0).map((d) => d.driverId)),
    escapeShift,
  };
}

export function rerateDriver(args: {
  card: DriverCard;
  stat: DriverSeasonStats;
  dice: Dice;
  context: RerateContext;
  crewGrade: PitCrewGrade;
  teamId?: string;
  /** D-033. Default 1; `null` reads SAG/6 literally. */
  pedalStepCap?: number | null;
  /** D-033. Default 'published'. */
  brakeSource?: 'published' | 'remainder';
}): DriverRerate {
  const { card, stat, dice, context, crewGrade, teamId } = args;
  const pedalStepCap = args.pedalStepCap === undefined ? 1 : args.pedalStepCap;
  const brakeSource = args.brakeSource ?? 'published';
  const steps: RerateStep[] = [];
  const step = (field: string, from: unknown, to: unknown, ruleCitation: string, basis: string): void => {
    steps.push({
      field,
      from: String(from),
      to: String(to),
      ruleCitation,
      basis,
      changed: String(from) !== String(to),
    });
  };

  // --- SAG/6 total pedals --------------------------------------------------
  const pedalDie = dice.d6();
  const total = totalPedalsFor(stat.rank, pedalDie);
  const gasRow = gasPedalsFor(stat.rank);
  const target = splitPedals(total.total, gasRow.gas);

  // D-033 — the band is the destination, not this winter's card.
  const gas = capStep(card.gasPedal, target.gas, pedalStepCap);
  let brake = capStep(card.brakePedal, target.brake, pedalStepCap);
  const stepped = pedalStepCap !== null && (gas !== target.gas || brake !== target.brake);

  step(
    'gas pedal',
    card.gasPedal,
    gas,
    CITE_GAS,
    `${gasRow.band} → ${gasRow.gas} gas${gas !== target.gas ? ` (a step at a time — D-033; still climbing toward ${target.gas})` : ''}`,
  );
  step(
    'brake pedal',
    card.brakePedal,
    brake,
    CITE_PEDAL_TOTAL,
    `${total.band} → ${total.total} total${total.rolled ? ` (1d6 ${pedalDie})` : ''}${target.adjusted ? `; ${target.adjusted}` : ''}${brake !== target.brake ? ` (stepped — D-033; heading for ${target.brake})` : ''}`,
  );
  void stepped;

  // --- SAG/7 how many qualities -------------------------------------------
  const qualityRoll = rollPercentile(dice);
  const count = qualityCountFor(total.total, qualityRoll.value);
  step(
    'qualities',
    card.qualities.length,
    count.count,
    CITE_QUALITY_COUNT,
    `${total.total} pedals, d100 ${qualityRoll.value} in ${count.band}`,
  );

  // --- D-033: the brake pedal, drawn rather than subtracted ----------------
  if (brakeSource === 'published') {
    const brakeRoll = rollPercentile(dice);
    const brakeBand = lookup(CAL_BRAKE_PEDALS[count.count as 1 | 2 | 3 | 4 | 5], brakeRoll.value);
    const drawn = capStep(card.brakePedal, brakeBand.value, pedalStepCap);
    const index = steps.findIndex((x) => x.field === 'brake pedal');
    steps[index] = {
      field: 'brake pedal',
      from: String(card.brakePedal),
      to: String(drawn),
      ruleCitation: `${CITE_BRAKE_DRAWN}`,
      basis: `${count.count} qualities, d100 ${brakeRoll.value} → ${brakeBand.value}${drawn !== brakeBand.value ? ' (stepped — D-033)' : ''}`,
      changed: card.brakePedal !== drawn,
    };
    brake = drawn;
  }

  // --- SAG/7 which qualities ----------------------------------------------
  const { qualities, earned } = assignQualities({
    dice,
    count: count.count,
    stat,
    context,
  });
  step(
    'quality list',
    card.qualities.join('/') || '—',
    qualities.join('/') || '—',
    CITE_QUALITY_ASSIGN,
    earned.length > 0 ? `earned: ${earned.join(', ')}` : 'all rolled',
  );

  // --- SAG/7 stars ---------------------------------------------------------
  const starred = assignStars({ qualities, stat, context });
  step(
    'starred',
    card.starred.join('/') || '—',
    starred.join('/') || '—',
    CITE_STARRED,
    starred.length > 0 ? starReason(starred, stat, context) : 'none earned',
  );

  // --- SAG/8 trophy --------------------------------------------------------
  const startRank = context.startRank.get(card.driverId);
  const trophy = startRank === undefined ? null : trophyForStartRank(startRank);
  step(
    'trophy',
    card.trophy ?? 'none',
    trophy ?? 'none',
    CITE_TROPHY,
    startRank === undefined
      ? `${stat.dnqs} DNQ — no trophy (SAG/8)`
      : `${ordinal(startRank)} on average start (${stat.avgStart?.toFixed(1)})`,
  );

  // --- SAG/8 fast pit ------------------------------------------------------
  const fastPitDie = dice.d6();
  const fastPit = fastPitFor(stat.rank, fastPitDie);
  step('fast pit', card.fastPit, fastPit.value, CITE_FAST_PIT, `${fastPit.band}, 1d6 ${fastPitDie}`);

  // --- SAG/8 escape, evidence-weighted (D-008) -----------------------------
  const escape = evidenceWeightedRerate<number>({
    table: ESCAPE_TABLE,
    successes: stat.troubleEscapes,
    attempts: stat.troubleAttempts,
    currentRating: card.escape,
    rateShift: context.escapeShift,
  });
  step('escape', card.escape, escape.rating, CITE_ESCAPE, escape.explanation);

  // --- D-007 crew grade ----------------------------------------------------
  step('crew grade', card.pitCrewGrade, crewGrade, CITE_PIT_CREW, "the shop's finish rate, not his");

  const after: DriverCard = {
    ...card,
    gasPedal: gas,
    brakePedal: brake,
    qualities,
    starred,
    trophy,
    fastPit: fastPit.value,
    // The EZRA double-fast-pit split is a property of the crew, and the crew
    // just moved. Cleared where the grade fell, kept where it held.
    fastPitDouble: crewGrade === card.pitCrewGrade ? card.fastPitDouble : null,
    escape: escape.rating,
    pitCrewGrade: crewGrade,
    ...(teamId ? { teamId } : {}),
  };

  return { driverId: card.driverId, name: card.name, rank: stat.rank, before: card, after, steps, story: tellIt(card, after, stat, steps) };
}

/**
 * D-EZ-93 — one winter of the age curve, laid on top of a finished re-rate.
 * Recorded as its own step so the card's history says which change the season
 * earned and which the calendar took.
 */
function applyAging(result: DriverRerate, dice: Dice): void {
  const age = ageOf(result.before) + 1;
  const step = agingStep(result.after, age, dice);
  result.aging = step;
  const after: DriverCard = {
    ...result.after,
    age,
    gasPedal: result.after.gasPedal + step.gas,
    escape: result.after.escape + step.escape,
  };
  result.steps.push({
    field: 'age',
    from: String(ageOf(result.before)),
    to: String(age),
    ruleCitation: CITE_AGING,
    basis:
      step.kind === 'none'
        ? step.note
        : `${step.note}: ${step.gas !== 0 ? `gas ${step.gas > 0 ? '+' : ''}${step.gas}` : `ESCAPE ${step.escape > 0 ? '+' : ''}${step.escape}`}`,
    changed: true,
  });
  if (step.kind !== 'none') {
    result.story += step.kind === 'growth' ? ` At ${age} he is still getting better.` : ` At ${age} the years are starting to tell.`;
  }
  result.after = after;
}

/**
 * SAG/7 — "The driver should be assigned certain qualities before others up to
 * his deserved number."
 *
 * STAR and HEAT are EARNED and are dealt first; whatever room is left is rolled
 * on the Quality by Driver table with STAR and HEAT re-rolled out, because they
 * have already been decided on merit and cannot also come up by chance.
 *
 * If a driver earns more than he deserves — a top-ten finisher who is also in
 * the top quarter for aggression, on a two-pedal card that allows one quality —
 * the printed order decides: STAR is named first on the page, so STAR is kept.
 * That is a reading, and it is flagged as one.
 */
function assignQualities(args: {
  dice: Dice;
  count: number;
  stat: DriverSeasonStats;
  context: RerateContext;
}): { qualities: Quality[]; earned: Quality[] } {
  const { dice, count, stat, context } = args;
  const earned: Quality[] = [];

  // "give a STAR to all top 10 drivers, to the top 11-18 drivers that won a
  // race, and to other drivers from 19th and below … if they won a race."
  const wonARace = context.raceWinners.has(stat.driverId);
  if (stat.rank <= 10 || wonARace) earned.push('STAR');
  if (context.heatSet.has(stat.driverId)) earned.push('HEAT');

  const qualities: Quality[] = [];
  for (const q of earned) {
    if (qualities.length >= count) break;
    qualities.push(q);
  }

  const banned = new Set<Quality>(['STAR', 'HEAT']);
  let guard = 0;
  while (qualities.length < count && guard < 500) {
    guard += 1;
    const p = rollPercentile(dice);
    const band = lookup(QUALITY_BY_DRIVER as RollTable<Quality>, p.value);
    const q = band.value;
    if (banned.has(q)) continue; // "Re-roll any STAR and HEAT rolls."
    if (qualities.includes(q)) continue;
    qualities.push(q);
  }

  // A pathological run of duplicates must not return a short card.
  if (qualities.length < count) {
    for (const q of QUALITIES) {
      if (qualities.length >= count) break;
      if (!qualities.includes(q) && !banned.has(q)) qualities.push(q);
    }
  }

  return { qualities, earned: earned.filter((q) => qualities.includes(q)) };
}

/**
 * SAG/7 — starred qualities are "in addition to their total number of
 * qualities", so they do not consume a slot; but a star can only be worn on a
 * quality the driver actually has, so the earned star is added to the list when
 * it is missing.
 */
function assignStars(args: {
  qualities: Quality[];
  stat: DriverSeasonStats;
  context: RerateContext;
}): Quality[] {
  const { qualities, stat, context } = args;
  const starred: Quality[] = [];

  if (context.heatKing === stat.driverId) {
    if (!qualities.includes('HEAT')) qualities.push('HEAT');
    starred.push('HEAT');
  }
  if (context.starredStar.has(stat.driverId)) {
    if (!qualities.includes('STAR')) qualities.push('STAR');
    if (!starred.includes('STAR')) starred.push('STAR');
  }

  // AMBIGUITY (SAG/7). The page ends "If the driver still deserves remaining
  // qualities or starred qualities, roll for these randomly on the Quality by
  // Driver Table and Creating Drivers: Starred Qualities" — and never says how
  // many further stars a driver DESERVES. The two named rules above (starred
  // HEAT to the most aggressive driver, starred STAR to the champion and the
  // biggest earner) are exhaustive as written.
  //
  // Reading taken: no additional stars are rolled. Every star in this league is
  // earned by name and belongs to at most three drivers a season. The
  // alternative — rolling the inaugural star table for everybody every winter —
  // would compound: stars are never removed by any printed rule, so the league
  // would be carrying a hundred of them inside three seasons and the mechanic
  // would stop meaning anything.
  return starred;
}

function starReason(starred: Quality[], stat: DriverSeasonStats, context: RerateContext): string {
  const bits: string[] = [];
  if (starred.includes('HEAT')) bits.push('most instances of trouble started');
  if (starred.includes('STAR')) {
    bits.push(stat.rank === 1 ? 'champion' : 'most winnings or the biggest merchandise brand');
  }
  void context;
  return bits.join('; ');
}

// ---------------------------------------------------------------------------
// 5. Rookies (SAG/5)
// ---------------------------------------------------------------------------

function signRookies(args: {
  count: number;
  dice: Dice;
  existing: readonly DriverCard[];
  seasonNumber: number;
  /** D-EZ-93 — give each rookie an age (hashed, no dice spent). */
  withAge?: boolean;
}): { rookies: GeneratedDriver[]; cards: DriverCard[] } {
  const { count, dice, existing, seasonNumber } = args;
  const rookies: GeneratedDriver[] = [];
  const cards: DriverCard[] = [];

  const usedNames = new Set(existing.map((c) => c.name));
  const usedNumbers = new Set(existing.map((c) => c.carNumber));
  const usedIds = new Set(existing.map((c) => c.driverId));

  for (let i = 0; i < count; i += 1) {
    const name = nextFreeName(dice, usedNames);
    usedNames.add(name);
    const carNumber = nextFreeNumber(dice, usedNumbers);
    usedNumbers.add(carNumber);

    let driverId = `drv-s${seasonNumber + 1}-${String(i + 1).padStart(2, '0')}`;
    let n = 1;
    while (usedIds.has(driverId)) driverId = `drv-s${seasonNumber + 1}-${String(i + 1).padStart(2, '0')}x${n++}`;
    usedIds.add(driverId);

    // SAG/5 — the INAUGURAL tables, not the re-rate ones. A rookie has no
    // season to be re-rated from.
    const created = createDriver(dice, { driverId, name, carNumber });
    const rookie = args.withAge
      ? { ...created, card: { ...created.card, age: rookieAge(`${driverId}|${name}`) } }
      : created;
    rookies.push(rookie);
    cards.push(rookie.card);
  }

  return { rookies, cards };
}

// ---------------------------------------------------------------------------
// 6. Seating next season's garage
// ---------------------------------------------------------------------------

/**
 * Every chartered seat that emptied over the winter is filled, in order, from
 * whoever is not already on a team. The charter keeps its size: a shop that ran
 * three cars runs three cars, because the charter is the thing that persists
 * and the drivers are what move through it.
 */
function seatTheGarage(args: {
  teams: readonly Team[];
  roster: readonly DriverCard[];
  crewGrades: Record<string, PitCrewGrade>;
}): { teams: Team[]; roster: DriverCard[] } {
  const { teams, roster, crewGrades } = args;
  const byId = new Map(roster.map((c) => [c.driverId, c]));
  const seated = new Set<string>();
  const nextTeams: Team[] = [];

  const free = roster.filter((c) => !teams.some((t) => t.driverIds.includes(c.driverId)));
  let freeIndex = 0;

  for (const team of teams) {
    const kept = team.driverIds.filter((id) => byId.has(id));
    const driverIds = [...kept];
    while (driverIds.length < TEAM_SIZE && freeIndex < free.length) {
      const candidate = free[freeIndex++];
      if (seated.has(candidate.driverId)) continue;
      driverIds.push(candidate.driverId);
    }
    for (const id of driverIds) seated.add(id);
    nextTeams.push({ ...team, driverIds });
  }

  // Stamp the team and its crew grade onto every card, so a card is readable
  // on its own exactly as a printed one is.
  const nextRoster = roster.map((card) => {
    const team = nextTeams.find((t) => t.driverIds.includes(card.driverId));
    if (!team) return { ...card, teamId: undefined };
    return {
      ...card,
      teamId: team.teamId,
      pitCrewGrade: crewGrades[team.teamId] ?? card.pitCrewGrade,
    };
  });

  return { teams: nextTeams, roster: nextRoster };
}

// ---------------------------------------------------------------------------
// Narration
// ---------------------------------------------------------------------------

/**
 * One or two sentences the feature writer can print unedited. Grounded in the
 * steps that actually changed — it can say nothing the ledger does not already
 * contain (D-003).
 */
function tellIt(before: DriverCard, after: DriverCard, stat: DriverSeasonStats, steps: RerateStep[]): string {
  const changed = steps.filter((s) => s.changed);
  if (changed.length === 0) {
    return `${after.name} comes back next season on exactly the card he ran this one — ${ordinal(stat.rank)} in the standings and nothing to argue with.`;
  }

  const gas = after.gasPedal - before.gasPedal;
  const qualities = after.qualities.length - before.qualities.length;

  const opening =
    gas > 0
      ? `${after.name} gets a faster car. `
      : gas < 0
        ? `${after.name} loses a step. `
        : `${after.name}'s card is re-drawn. `;

  const bits: string[] = [];
  if (gas !== 0) bits.push(`${before.gasPedal} gas to ${after.gasPedal}`);
  if (after.brakePedal !== before.brakePedal) bits.push(`${before.brakePedal} brake to ${after.brakePedal}`);
  if (qualities !== 0) bits.push(`${before.qualities.length} qualities to ${after.qualities.length}`);
  if (after.trophy !== before.trophy) {
    bits.push(after.trophy ? `a ${after.trophy} trophy` : 'no trophy at all');
  }
  if (after.escape !== before.escape) bits.push(`ESCAPE ${before.escape} to ${after.escape}`);
  if (after.pitCrewGrade !== before.pitCrewGrade) bits.push(`a grade ${after.pitCrewGrade} crew`);

  const body = bits.length > 0 ? `${bits.join(', ')}. ` : '';
  const close = `He finished ${ordinal(stat.rank)}${stat.wins > 0 ? ` with ${stat.wins} win${stat.wins === 1 ? '' : 's'}` : ''}.`;

  return `${opening}${body}${close}`;
}

// ---------------------------------------------------------------------------
// Small helpers
// ---------------------------------------------------------------------------

function makeSeededPick(seed: number): <T>(pool: readonly T[]) => T {
  let s = (seed ^ 0x9e37_79b9) >>> 0;
  return <T>(pool: readonly T[]): T => {
    s = (s + 0x6d2b_79f5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    const r = ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    return pool[Math.floor(r * pool.length)];
  };
}

function newTeamName(pick: <T>(pool: readonly T[]) => T, used: Set<string>): string {
  const shapes: readonly (() => string)[] = [
    () => `${pick(SURNAMES)} & ${pick(SURNAMES)} Motorsports`,
    () => `${pick(TRADES)} Racing`,
    () => `${pick(SURNAMES)} Brothers`,
    () => `${pick(SURNAMES)} Motorsports`,
    () => `${pick(TRADES)} ${pick(SURNAMES)} Racing`,
  ];
  for (let attempt = 0; attempt < 400; attempt += 1) {
    const candidate = pick(shapes)();
    if (/^(.+) & \1 Motorsports$/.test(candidate)) continue;
    if (!used.has(candidate)) return candidate;
  }
  for (const trade of TRADES) {
    for (const surname of SURNAMES) {
      const candidate = `${trade} ${surname} Racing`;
      if (!used.has(candidate)) return candidate;
    }
  }
  throw new Error('team name pools exhausted');
}

function nextFreeName(dice: Dice, used: Set<string>): string {
  for (let attempt = 0; attempt < 400; attempt += 1) {
    const first = FIRST_NAMES[dice.d(FIRST_NAMES.length) - 1];
    const last = SURNAMES[dice.d(SURNAMES.length) - 1];
    const candidate = `${first} ${last}`;
    if (!used.has(candidate)) return candidate;
  }
  for (const first of FIRST_NAMES) {
    for (const last of SURNAMES) {
      const candidate = `${first} ${last}`;
      if (!used.has(candidate)) return candidate;
    }
  }
  throw new Error('name pools exhausted');
}

function nextFreeNumber(dice: Dice, used: Set<string>): string {
  for (let attempt = 0; attempt < 400; attempt += 1) {
    const candidate = CAR_NUMBERS[dice.d(CAR_NUMBERS.length) - 1];
    if (!used.has(candidate)) return candidate;
  }
  for (const candidate of CAR_NUMBERS) if (!used.has(candidate)) return candidate;
  throw new Error('car number pool exhausted');
}

function ordinal(n: number): string {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] ?? s[v] ?? s[0]);
}
